/** @format */

/**
 * ============================================================================
 * AI CHATBOT ARCHITECTURE ANALYSIS (Step 2 – Voice Mode prep)
 * ============================================================================
 *
 * Component: AiChatbot (this file)
 *   - Floating FAB + slide-up panel UI ("Ask your data").
 *   - Mounted globally in Main.js when canShowAiChatbot() is true (subscription +
 *     role.permission_config.showAiChatbot). Not route-scoped.
 *   - Supporting UI: AiChatEChart.js, buildAiChatEChartsOption.js, AiChatbot.css.
 *   - State: messages[], input, loading, multi-step flows (document gen, create record),
 *     file upload options. No WebSocket; all request/response over HTTP.
 *
 * Message send:
 *   - handleSend() — primary text entry (Enter or Send button via handleKeyDown).
 *     Appends { role: 'user', text } then branches on local intent detection before
 *     calling the backend. Does NOT send prior messages to OpenAI (stateless per turn).
 *   - handleFileChange() — file attach; base64 POST to /ai-chat/parse-file.
 *   - handleDocumentObjectChoice / handleCreateObjectChoice — quick-reply shortcuts.
 *   - Local-only paths (no API): create-record intent → EditLayoutModal;
 *     document/quote intent → GenerateDocumentApi; multi-step object/ID prompts.
 *
 * Response handling:
 *   - All replies appended via setMessages() as { role: 'assistant', ... }.
 *   - Message shape variants:
 *       text          — plain assistant reply
 *       error         — user-visible failure
 *       data          — SQL result rows; rendered as table and/or ECharts chart
 *       columnLabels, rowCount, showChart, chartType, objectName — table/chart metadata
 *       documentParse — file extraction (parsedText, transformedOutput, resultFormat)
 *       attachmentBlobUrl / attachmentFilename — generated PDF preview/download
 *       quickReplyOptions / quickReplyAction — guided follow-up buttons
 *   - Loading: loading + loadingMessage; typing indicator in message list.
 *   - Errors: err.response?.data?.message from axios, else err.message.
 *
 * Conversation storage:
 *   - Browser localStorage only (key: ai-sql-chatbot-history). No server-side chat log.
 *   - loadHistory() on mount; saveHistory() in useEffect on messages change.
 *   - messagesForStorage() strips blob URLs and truncates large parsedText/transformedOutput
 *     (max 12k chars) before persist. Clear-chat removes the key.
 *   - Voice mode note: history is client-only; any voice transcript integration should
 *     reuse the same messages[] shape and saveHistory pipeline.
 *
 * OpenAI integration (backend only — frontend never calls OpenAI directly):
 *   - Natural-language queries: POST /api/objectconfig/ai-query/execute { question }
 *       → AiDataQueryService.generatePlan() → DataQueryPlan JSON
 *       → fetchRelatedRecords (list) or AiSqlService fallback (aggregate)
 *       → { success, data, rowCount, columnLabels, objectName, message }
 *   - File JSON/CSV transform: POST /api/objectconfig/ai-chat/parse-file
 *       → PdfReaderService (OCR/vision may use OpenAI) + optional
 *       AiDocumentTransformService.transformExtractedText() (chat/completions)
 *   - Schema context: EmbeddingService (text-embedding-3-small) + pgvector via
 *     AiSqlService.getRelevantSchema(); env OPENAI_API_KEY / CHATGPT_API_KEY.
 *   - Auth: DataApi adds CRM JWT from localStorage on every request.
 *
 * Voice mode (Step 3–5):
 *   - speechRecognition.js — browser SpeechRecognition / webkitSpeechRecognition.
 *   - Mic on → Listening…; speech detected → Speaking…; pause → Listening… again.
 *   - Send → Processing… while awaiting result; draft stays in input until Send.
 *   - No separate voice endpoints; permissions/history/tools/OpenAI unchanged.
 *   - TTS: read-aloud button on assistant text content (not charts/tables).
 * ============================================================================
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import DataApi from '../../api/DataApi';
import GenerateDocumentApi from '../../api/GenerateDocumentApi';
import EditLayoutModal from '../layout/EditLayoutModal';
import CurrencyApi from '../../api/CurrencyApi';
import {
  formatDate,
  formatDateTime,
  formatFetchRecordsFieldForDisplay,
  formatTime,
} from '../../utils/formatUtils';
import { getMultiCurrencySettings } from '../../utils/multiCurrencySettings';
import { buildExchangeRateLookupFromCurrencyList } from '../../utils/reportCurrencyAggregate';
import AiChatEChart from './AiChatEChart';
import {
  columnSupportsTableTotal,
  formatAiChatFlatAggregateCurrency,
  formatAiChatTableTotal,
} from './aiChatCurrencyUtils';
import {
  detectNestedIncludeKeys,
  formatIncludeCellSummary,
  mapQueryApiResponseToMessage,
  resolveQueryTableColumns,
  sanitizeResultTitleForFilename,
} from './aiChatQueryUtils';
import { detectChartTypeFromQuestion, wantsChartFromQuestion } from './buildAiChatEChartsOption';
import {
  createSpeechRecognition,
  getSpeechRecognitionErrorMessage,
  isSpeechRecognitionSupported,
  parseSpeechRecognitionResults,
} from './speechRecognition';
import { inferFileUploadFromPrompt } from './parseFileUploadPrompt';
import { formatDashboardOwner } from '../dashboard/dashboardFormUtils';
import { pickRecordId } from '../../utils/dashboardRunUserLabel';
import {
  getAssistantSpeakableText,
  isTextToSpeechSupported,
  speakText,
  stopTextToSpeech,
} from './textToSpeech';
import './AiChatbot.css';

/** Object options for "create record" (label -> objectName) */
const CREATE_RECORD_OBJECTS = [
  { label: 'Lead', objectName: 'lead' },
  { label: 'Opportunity', objectName: 'opportunity' },
  { label: 'Quote', objectName: 'quote' },
  { label: 'Contact', objectName: 'contact' },
  { label: 'Account', objectName: 'account' },
  { label: 'Task', objectName: 'task' },
];

const CHATBOT_NAME = 'Zeni';
const CHATBOT_TAGLINE = 'Your AI Assistant';
const CHATBOT_LAUNCH_LABEL = `Ask ${CHATBOT_NAME}`;

/** Quick options shown when the user sends a greeting (hi, hello, hey, etc.) */
const GREETING_QUICK_OPTIONS = [
  'List my open opportunities',
  'Show tasks due this week',
  'List my reports',
  'List my dashboards',
  'Create a new lead',
  'Generate a quote document',
];

const GREETING_WORDS = new Set(['hi', 'hello', 'hey', 'hiya', 'howdy', 'morning', 'afternoon', 'evening']);

/** True when the message is only greetings — e.g. hi, hello, hey, hi hello, hi hi hi */
const isGreetingMessage = (text) => {
  let t = (text || '').trim().toLowerCase();
  if (!t) return false;
  t = t.replace(/[!.,?]+$/g, '').replace(/[,;]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (/^good\s+(morning|afternoon|evening)$/.test(t)) return true;
  const tokens = t.split(' ').filter(Boolean);
  if (!tokens.length) return false;
  return tokens.every((token) => GREETING_WORDS.has(token));
};

/** Match path like /view/:objectName/:id (supports custom objects e.g. lead__x) */
const getRecordContextFromPath = (pathname) => {
  if (!pathname || typeof pathname !== 'string') return null;
  const match = pathname.match(/^\/view\/([^/]+)\/([^/]+)\/?$/i);
  if (!match) return null;
  try {
    return {
      objectName: decodeURIComponent(match[1]).trim(),
      recordId: decodeURIComponent(match[2]).trim(),
    };
  } catch {
    return { objectName: match[1].trim(), recordId: match[2].trim() };
  }
};

/** Map URL object name to document API object (lead, opportunity, quote) */
const normalizeDocumentObjectName = (objectName) => {
  if (!objectName || typeof objectName !== 'string') return null;
  const base = objectName.trim().toLowerCase().split('__')[0];
  if (base === 'lead' || base === 'opportunity' || base === 'quote') return base;
  return null;
};

const getDocumentContextFromPath = (pathname) => {
  const ctx = getRecordContextFromPath(pathname);
  if (!ctx?.recordId) {
    return { leadId: null, opportunityId: null, quoteId: null };
  }
  const kind = normalizeDocumentObjectName(ctx.objectName);
  return {
    leadId: kind === 'lead' ? ctx.recordId : null,
    opportunityId: kind === 'opportunity' ? ctx.recordId : null,
    quoteId: kind === 'quote' ? ctx.recordId : null,
  };
};

/** Parse user reply to "choose object" step: returns 'lead' | 'opportunity' | 'quote' | null */
const parseDocumentObjectChoice = (text) => {
  const t = (text || '').trim().toLowerCase();
  if (/^leads?$/.test(t)) return 'lead';
  if (/^opportunit(y|ies)$/.test(t)) return 'opportunity';
  if (/^quotes?$/.test(t)) return 'quote';
  return null;
};

/** Detect if user is asking to generate a quote document */
const isGenerateQuoteIntent = (text) => {
  const t = (text || '').trim().toLowerCase();
  return (
    /\bgenerate\s*(the\s+)?(a\s+)?(customer\s+)?quote\b/.test(t) ||
    /\bquote\s*document\b/.test(t) ||
    /\bquote\s*pdf\b/.test(t) ||
    /\bcreate\s*(a\s+)?quote\s*(pdf|document)?\b/.test(t) ||
    /\bdownload\s*(a\s+)?quote\b/.test(t) ||
    /\bget\s*(a\s+)?quote\s*(pdf|document)?\b/.test(t) ||
    /\bprint\s*(a\s+)?quote\b/.test(t) ||
    (/\bquotation\b/.test(t) && /\b(generate|download|get|print|create)\b/.test(t))
  );
};

const isDocumentOrQuoteIntentMessage = (text) =>
  isGenerateQuoteIntent(text) || isGenerateDocumentIntent(text);

/** User wants the permission-filtered report catalog (same rules as /reports page). */
const detectListReportsIntent = (text) => {
  const t = (text || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!/\breports?\b/.test(t)) return false;
  return (
    /\b(list|show|give|display|view|get|fetch)\b.*\breports?\b/.test(t) ||
    /\breports?\b.*\b(list|show)\b/.test(t) ||
    /\ball\s+(created\s+)?reports?\b/.test(t) ||
    /\bcreated\s+reports?\b/.test(t) ||
    /\bmy\s+reports?\b/.test(t) ||
    /\breport\s+list\b/.test(t) ||
    /\breports?\s+i\s+(have|can\s+see|created)\b/.test(t)
  );
};

const REPORT_LIST_COLUMN_LABELS = {
  report_label: 'Report',
  object_name: 'Object',
  report_type: 'Type',
  owner: 'Owner',
  visibility: 'Visibility',
  createddate: 'Created',
};

const DASHBOARD_LIST_COLUMN_LABELS = {
  dashboard_label: 'Dashboard',
  name: 'Name',
  owner: 'Owner',
  view_as: 'View as',
  createddate: 'Created',
};

/** Serializable catalog link keys — functions cannot be stored in localStorage history. */
const CATALOG_LINK_REPORT = 'report';
const CATALOG_LINK_DASHBOARD = 'dashboard';

const CATALOG_LINK_CONFIGS = {
  [CATALOG_LINK_REPORT]: {
    labelColumn: 'report_label',
    getUrl: (row) => (row?.id ? `/viewreport/${row.id}?source=reports` : null),
  },
  [CATALOG_LINK_DASHBOARD]: {
    labelColumn: 'dashboard_label',
    getUrl: (row) => (row?.id ? `/dashboard/${row.id}/run` : null),
  },
};

const normalizeCatalogLinkKey = (catalogLink) => {
  if (!catalogLink) return null;
  if (typeof catalogLink === 'string') {
    return CATALOG_LINK_CONFIGS[catalogLink] ? catalogLink : null;
  }
  if (catalogLink.labelColumn === 'report_label') return CATALOG_LINK_REPORT;
  if (catalogLink.labelColumn === 'dashboard_label') return CATALOG_LINK_DASHBOARD;
  return null;
};

const resolveCatalogLinkConfig = (catalogLink) => {
  const key = normalizeCatalogLinkKey(catalogLink);
  return key ? CATALOG_LINK_CONFIGS[key] : null;
};

const rehydrateStoredMessage = (message) => {
  if (!message || typeof message !== 'object') return message;
  const next = { ...message };
  if (Array.isArray(next.catalogSections)) {
    next.catalogSections = next.catalogSections.map((section) => {
      let catalogLink = normalizeCatalogLinkKey(section.catalogLink);
      if (!catalogLink && typeof section.title === 'string') {
        const titleLower = section.title.toLowerCase();
        if (titleLower.startsWith('report')) catalogLink = CATALOG_LINK_REPORT;
        else if (titleLower.startsWith('dashboard')) catalogLink = CATALOG_LINK_DASHBOARD;
      }
      return { ...section, catalogLink };
    });
  }
  if (next.catalogLink) {
    next.catalogLink = normalizeCatalogLinkKey(next.catalogLink);
  }
  return next;
};

const formatReportsForChatTable = (reports) =>
  (reports || []).map((r) => ({
    id: pickRecordId(r) || r.id,
    report_label: r.report_label || r.name || '—',
    object_name:
      (r.report_objects && r.report_objects.length > 0
        ? r.report_objects.map((o) => o.object_label || o.object_name).join(' & ')
        : null) ||
      r.object_label ||
      r.object_name ||
      '—',
    report_type: r.report_type || '—',
    owner: formatDashboardOwner(r) || '—',
    visibility: r.is_public ? 'Public' : 'Private',
    createddate: r.createddate ? formatDateTime(r.createddate) : '—',
  }));

/** User wants the permission-filtered dashboard catalog (same rules as /dashboards page). */
const detectListDashboardsIntent = (text) => {
  const t = (text || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!/\bdashboards?\b/.test(t)) return false;
  return (
    /\b(list|show|give|display|view|get|fetch)\b.*\bdashboards?\b/.test(t) ||
    /\bdashboards?\b.*\b(list|show)\b/.test(t) ||
    /\ball\s+(created\s+)?dashboards?\b/.test(t) ||
    /\bcreated\s+dashboards?\b/.test(t) ||
    /\bmy\s+dashboards?\b/.test(t) ||
    /\bdashboard\s+list\b/.test(t) ||
    /\bdashboards?\s+i\s+(have|can\s+see|created)\b/.test(t)
  );
};

const formatDashboardsForChatTable = (dashboards) =>
  (dashboards || []).map((d) => ({
    id: pickRecordId(d) || d.id,
    dashboard_label: d.label || d.name || '—',
    name: d.name || '—',
    owner: formatDashboardOwner(d) || '—',
    view_as:
      d.view_type === 'LOGGED_IN_USER'
        ? 'Logged-in viewer'
        : d.running_user_full_name
          ? `${d.running_user_full_name}`.trim()
          : 'Fixed user',
    createddate: d.createddate ? formatDateTime(d.createddate) : '—',
  }));

/** Detect if user is asking to generate/download a document (e.g. for opportunity) */
const isGenerateDocumentIntent = (text) => {
  const t = (text || '').trim().toLowerCase();
  return (
    /\bgenerate\s*(a\s+)?document\b/.test(t) ||
    /\bdownload\s*(a\s+)?document\b/.test(t) ||
    /\bget\s*(a\s+)?document\b/.test(t) ||
    /\bcreate\s*(a\s+)?document\b/.test(t) ||
    (/\bdocument\b/.test(t) && (/\bgenerate\b/.test(t) || /\bdownload\b/.test(t) || /\bget\b/.test(t)))
  );
};

/**
 * Single intelligent detector for "create/insert/add/new record" intent.
 * Handles many phrasings and extracts object name when present.
 * @param {string} text - User message
 * @returns {{ isCreateIntent: boolean, objectName: string | null }}
 */
const detectCreateRecordIntent = (text) => {
  const t = (text || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!t) return { isCreateIntent: false, objectName: null };

  const verbMatch = t.match(/^(create|add|insert|new)\b/);
  if (!verbMatch) return { isCreateIntent: false, objectName: null };

  // 1. "create record for school" / "add record for opportunity" / "insert record for lead"
  let m = t.match(/\b(?:create|add|insert|new)\s+(?:a\s+)?(?:new\s+)?record\s+for\s+(\w+)/);
  if (m && m[1]) return { isCreateIntent: true, objectName: m[1] };

  // 2. "create school record" / "add opportunity record" / "insert lead record" / "new contact record"
  m = t.match(/\b(?:create|add|insert|new)\s+(?:a\s+)?(?:new\s+)?(\w+)\s+record\b/);
  if (m && m[1] && m[1] !== 'record') return { isCreateIntent: true, objectName: m[1] };

  // 3. "create record" / "insert record" / "add record" / "new record" (no object)
  if (/^(create|add|insert|new)\s+(?:a\s+)?(?:new\s+)?record?s?$/.test(t)) {
    return { isCreateIntent: true, objectName: null };
  }

  // 4. "create lead" / "insert opportunity" / "new school" / "add a contact" / "create a new task"
  m = t.match(/\b(?:create|add|insert|new)\s+(?:a\s+)?(?:new\s+)?(\w+)s?\b/);
  if (m && m[1]) {
    const obj = m[1];
    if (obj === 'record') return { isCreateIntent: true, objectName: null };
    return { isCreateIntent: true, objectName: obj };
  }

  return { isCreateIntent: true, objectName: null };
};

/** Parse user reply when we asked "which object to create": return objectName or null */
const parseCreateObjectChoice = (text) => {
  const t = (text || '').trim().toLowerCase();
  const found = CREATE_RECORD_OBJECTS.find(
    (o) => o.objectName === t || o.label.toLowerCase() === t || new RegExp(`^${o.objectName}s?$`).test(t)
  );
  return found ? found.objectName : null;
};

/** Conversation storage — see architecture block at top of file. */
const STORAGE_KEY = 'ai-sql-chatbot-history';
const FAB_POSITION_STORAGE_KEY = 'ai-chatbot-fab-position';
const FAB_POSITION_STORAGE_VERSION = 2;
const FAB_DRAG_THRESHOLD_PX = 5;
const FAB_VIEWPORT_MARGIN_PX = 8;

/** Pixels from the right edge of the screen — smaller value = button further right. */
const FAB_DEFAULT_INSET_RIGHT = 12;
/** Pixels from the bottom edge of the screen — smaller value = button further down. */
const FAB_DEFAULT_INSET_BOTTOM = 12;

const getDefaultFabPosition = () => ({
  right: FAB_DEFAULT_INSET_RIGHT,
  bottom: FAB_DEFAULT_INSET_BOTTOM,
});

const loadFabPosition = () => {
  try {
    const raw = localStorage.getItem(FAB_POSITION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.v !== FAB_POSITION_STORAGE_VERSION) return null;
    if (!parsed.customized) return null;
    if (typeof parsed?.right !== 'number' || typeof parsed?.bottom !== 'number') return null;
    return { right: parsed.right, bottom: parsed.bottom };
  } catch {
    return null;
  }
};

const saveFabPosition = (pos) => {
  try {
    localStorage.setItem(
      FAB_POSITION_STORAGE_KEY,
      JSON.stringify({
        v: FAB_POSITION_STORAGE_VERSION,
        right: pos.right,
        bottom: pos.bottom,
        customized: true,
      })
    );
  } catch {
    // ignore quota errors
  }
};

const clampFabPosition = (right, bottom, el) => {
  const margin = FAB_VIEWPORT_MARGIN_PX;
  const w = el?.offsetWidth || 160;
  const h = el?.offsetHeight || 48;
  return {
    right: Math.min(Math.max(margin, right), Math.max(margin, window.innerWidth - w - margin)),
    bottom: Math.min(Math.max(margin, bottom), Math.max(margin, window.innerHeight - h - margin)),
  };
};

const loadHistory = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(rehydrateStoredMessage) : [];
  } catch {
    return [];
  }
};

const PARSED_TEXT_STORAGE_MAX = 12000;

/** Strip blob URLs from messages before persisting (they are session-only and invalid after refresh) */
const messagesForStorage = (messages) => {
  if (!Array.isArray(messages)) return messages;
  return messages.map((m) => {
    const normalized = rehydrateStoredMessage(m);
    if (normalized && normalized.attachmentBlobUrl) {
      const { attachmentBlobUrl, ...rest } = normalized;
      return { ...rest, attachmentFilename: normalized.attachmentFilename };
    }
    if (normalized && normalized.documentParse) {
      let next = { ...normalized };
      if (typeof next.parsedText === 'string' && next.parsedText.length > PARSED_TEXT_STORAGE_MAX) {
        next.parsedText = `${next.parsedText.slice(0, PARSED_TEXT_STORAGE_MAX)}… [truncated for storage]`;
        next.parsedTextStorageTruncated = true;
      }
      if (
        typeof next.transformedOutput === 'string' &&
        next.transformedOutput.length > PARSED_TEXT_STORAGE_MAX
      ) {
        next.transformedOutput = `${next.transformedOutput.slice(0, PARSED_TEXT_STORAGE_MAX)}… [truncated for storage]`;
        next.transformedOutputStorageTruncated = true;
      }
      return next;
    }
    return normalized;
  });
};

const saveHistory = (messages) => {
  try {
    if (messages.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messagesForStorage(messages)));
  } catch {
    // ignore quota or parse errors
  }
};

/** Voice UI: idle | listening (mic on, waiting) | speaking (user talking) | processing (result). */
const VOICE_STATE = {
  IDLE: 'idle',
  LISTENING: 'listening',
  SPEAKING: 'speaking',
  PROCESSING: 'processing',
};

const VOICE_STATUS_LABELS = {
  [VOICE_STATE.LISTENING]: 'Listening…',
  [VOICE_STATE.SPEAKING]: 'Speaking…',
  [VOICE_STATE.PROCESSING]: 'Processing…',
};

/** After this pause with no new speech, UI returns from Speaking to Listening (mic stays on). */
const VOICE_SPEECH_PAUSE_MS = 1200;

const AiChatbot = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => loadHistory());
  /** Tracks empty vs non-empty only — avoids re-rendering the full panel on every keystroke. */
  const [inputHasText, setInputHasText] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Running query…');
  const [fullView, setFullView] = useState(null);
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  /** Unified document flow when not on detail page: 'object' = choose Lead/Opportunity/Quote, 'id' = provide record ID */
  const [pendingDocumentStep, setPendingDocumentStep] = useState(null);
  const [pendingDocumentObjectName, setPendingDocumentObjectName] = useState(null);
  /** Create record: when true, next message is treated as object choice (or user can click quick reply) */
  const [pendingCreateObject, setPendingCreateObject] = useState(false);
  /** Open EditLayoutModal for creating a record (objectName set when opening) */
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createModalObjectName, setCreateModalObjectName] = useState(null);
  /** File chosen via attach; sent with the next message (prompt optional). */
  const [pendingUploadFile, setPendingUploadFile] = useState(null);
  /** Voice UI: idle | speaking | processing */
  const [voiceState, setVoiceState] = useState(VOICE_STATE.IDLE);
  /** True while a voice session is active (mic used this turn until send completes). */
  const [voiceSessionActive, setVoiceSessionActive] = useState(false);
  /** User-facing STT error (unsupported browser, permission denied, etc.). */
  const [speechError, setSpeechError] = useState(null);
  const speechSupported = useMemo(() => isSpeechRecognitionSupported(), []);
  const ttsSupported = useMemo(() => isTextToSpeechSupported(), []);
  /** Index of assistant message currently being read aloud, or null. */
  const [ttsSpeakingIndex, setTtsSpeakingIndex] = useState(null);
  /** Base currency + rates for fetchRecords money formatting (DetailLayout / list parity). */
  const [currencyDisplayCtx, setCurrencyDisplayCtx] = useState({
    baseCurrencyCode: null,
    exchangeRateLookup: null,
    multiCurrencyEnabled: true,
  });
  const currencyDisplayRef = useRef(currencyDisplayCtx);
  currencyDisplayRef.current = currencyDisplayCtx;
  const prevLoadingRef = useRef(false);
  const speechRecognitionRef = useRef(null);
  /** Input value when the current STT session started (preserves typed prefix). */
  const speechBaseInputRef = useRef('');
  /** Final transcript accumulated during the current STT session. */
  const speechSessionFinalRef = useRef('');
  /** When true, onend restarts recognition while the mic session is active. */
  const voiceListeningRef = useRef(false);
  const voiceSpeechPauseTimerRef = useRef(null);
  const inputTextRef = useRef('');
  const inputRef = useRef(null);
  const syncInputHasTextFlag = useCallback((value) => {
    const hasText = String(value ?? '').trim().length > 0;
    setInputHasText((prev) => (prev !== hasText ? hasText : prev));
  }, []);
  /** Update draft without re-rendering messages/charts (controlled input caused typing lag). */
  const syncChatInput = useCallback(
    (value) => {
      const next = value ?? '';
      inputTextRef.current = next;
      if (inputRef.current && inputRef.current.value !== next) {
        inputRef.current.value = next;
      }
      syncInputHasTextFlag(next);
    },
    [syncInputHasTextFlag]
  );
  const speakHelpersRef = useRef({
    getColumnLabel: (col) => col,
    formatCellValue: (_col, value) => (value == null ? '' : String(value)),
  });
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const fabAnchorRef = useRef(null);
  const fabDragRef = useRef({
    dragging: false,
    moved: false,
    startX: 0,
    startY: 0,
    originRight: 0,
    originBottom: 0,
    pointerId: null,
  });
  const [fabPosition, setFabPosition] = useState(() => {
    const saved = loadFabPosition();
    const defaults = getDefaultFabPosition();
    return clampFabPosition(
      saved?.right ?? defaults.right,
      saved?.bottom ?? defaults.bottom,
      null
    );
  });
  const [fabDragging, setFabDragging] = useState(false);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  useEffect(() => {
    const handleResize = () => {
      setFabPosition((prev) => clampFabPosition(prev.right, prev.bottom, fabAnchorRef.current));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const currencyApi = new CurrencyApi();
        const res = await currencyApi.fetchAll();
        const raw = res?.data?.data ?? res?.data;
        const list = Array.isArray(raw) ? raw : [];
        if (cancelled) return;
        const baseRow = list.find((r) => r.is_base_currency === true);
        const baseCurrencyCode = baseRow?.currency_code
          ? String(baseRow.currency_code).trim()
          : null;
        const exchangeRateLookup = buildExchangeRateLookupFromCurrencyList(list);
        let multiCurrencyEnabled = true;
        try {
          const s = await getMultiCurrencySettings();
          multiCurrencyEnabled = !!s.multiCurrencyEnabled;
        } catch {
          multiCurrencyEnabled = true;
        }
        if (!cancelled) {
          setCurrencyDisplayCtx({
            baseCurrencyCode,
            exchangeRateLookup,
            multiCurrencyEnabled,
          });
        }
      } catch {
        if (!cancelled) {
          setCurrencyDisplayCtx({
            baseCurrencyCode: null,
            exchangeRateLookup: null,
            multiCurrencyEnabled: true,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fabAlignStart = fabPosition.right > Math.max(220, window.innerWidth * 0.45);
  const panelOpensBelow = fabPosition.bottom > Math.max(400, window.innerHeight * 0.55);

  const handleFabPointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const d = fabDragRef.current;
    d.dragging = true;
    d.moved = false;
    d.startX = e.clientX;
    d.startY = e.clientY;
    d.originRight = fabPosition.right;
    d.originBottom = fabPosition.bottom;
    d.pointerId = e.pointerId;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleFabPointerMove = (e) => {
    const d = fabDragRef.current;
    if (!d.dragging || e.pointerId !== d.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && (Math.abs(dx) > FAB_DRAG_THRESHOLD_PX || Math.abs(dy) > FAB_DRAG_THRESHOLD_PX)) {
      d.moved = true;
      setFabDragging(true);
    }
    if (!d.moved) return;
    e.preventDefault();
    setFabPosition(
      clampFabPosition(d.originRight - dx, d.originBottom - dy, fabAnchorRef.current)
    );
  };

  const handleFabPointerUp = (e) => {
    const d = fabDragRef.current;
    if (!d.dragging || e.pointerId !== d.pointerId) return;
    d.dragging = false;
    setFabDragging(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // ignore if capture was already released
    }
    if (d.moved) {
      const next = clampFabPosition(
        d.originRight - (e.clientX - d.startX),
        d.originBottom - (e.clientY - d.startY),
        fabAnchorRef.current
      );
      setFabPosition(next);
      saveFabPosition(next);
      e.preventDefault();
      return;
    }
    setIsOpen((open) => !open);
  };

  const handleFabPointerCancel = () => {
    fabDragRef.current.dragging = false;
    fabDragRef.current.moved = false;
    setFabDragging(false);
  };

  const {
    leadId: currentLeadId,
    opportunityId: currentOpportunityId,
    quoteId: currentQuoteId,
  } = getDocumentContextFromPath(location?.pathname);

  const clearPendingDocumentFlow = () => {
    setPendingDocumentStep(null);
    setPendingDocumentObjectName(null);
  };

  const stopReadAloud = useCallback(() => {
    stopTextToSpeech();
    setTtsSpeakingIndex(null);
  }, []);

  const handleToggleReadAloud = useCallback(
    (messageIndex, msg) => {
      if (ttsSpeakingIndex === messageIndex) {
        stopReadAloud();
        return;
      }
      const text = getAssistantSpeakableText(msg, speakHelpersRef.current);
      if (!text) return;
      stopReadAloud();
      const started = speakText(text, {
        onStart: () => setTtsSpeakingIndex(messageIndex),
        onEnd: () => setTtsSpeakingIndex(null),
        onError: () => setTtsSpeakingIndex(null),
      });
      if (!started) {
        setSpeechError('Read aloud is not supported in this browser.');
      }
    },
    [ttsSpeakingIndex, stopReadAloud]
  );

  const clearSpeechPauseTimer = useCallback(() => {
    if (voiceSpeechPauseTimerRef.current) {
      clearTimeout(voiceSpeechPauseTimerRef.current);
      voiceSpeechPauseTimerRef.current = null;
    }
  }, []);

  const scheduleReturnToListening = useCallback(() => {
    clearSpeechPauseTimer();
    if (!voiceListeningRef.current) return;

    voiceSpeechPauseTimerRef.current = setTimeout(() => {
      voiceSpeechPauseTimerRef.current = null;
      if (voiceListeningRef.current) {
        setVoiceState(VOICE_STATE.LISTENING);
      }
    }, VOICE_SPEECH_PAUSE_MS);
  }, [clearSpeechPauseTimer]);

  /** Stop STT and commit the draft transcript to the chat input (no auto-send). */
  const stopSpeechRecognition = useCallback(() => {
    clearSpeechPauseTimer();
    voiceListeningRef.current = false;
    const recognition = speechRecognitionRef.current;
    if (recognition) {
      recognition.onresult = null;
      recognition.onend = null;
      recognition.onerror = null;
      try {
        recognition.abort();
      } catch (_) {
        try {
          recognition.stop();
        } catch (_) {
          // ignore
        }
      }
      speechRecognitionRef.current = null;
    }

    const draft = inputTextRef.current;
    speechBaseInputRef.current = draft;
    speechSessionFinalRef.current = '';
    if (draft) {
      syncChatInput(draft);
    }
  }, [clearSpeechPauseTimer, syncChatInput]);

  const startSpeechRecognition = useCallback(() => {
    if (!speechSupported) return false;

    stopSpeechRecognition();

    const recognition = createSpeechRecognition({
      continuous: true,
      interimResults: true,
    });
    if (!recognition) return false;

    speechRecognitionRef.current = recognition;
    speechBaseInputRef.current = inputTextRef.current;
    speechSessionFinalRef.current = '';

    recognition.onresult = (event) => {
      const { finalText, interimText } = parseSpeechRecognitionResults(event);
      if (finalText) {
        speechSessionFinalRef.current += finalText;
      }
      const spokenChunk = `${finalText}${interimText}`.trim();
      if (spokenChunk && voiceListeningRef.current) {
        setVoiceState(VOICE_STATE.SPEAKING);
        scheduleReturnToListening();
      }
      syncChatInput(
        speechBaseInputRef.current + speechSessionFinalRef.current + interimText
      );
    };

    recognition.onerror = (event) => {
      const message = getSpeechRecognitionErrorMessage(event.error);
      if (!message) return;

      voiceListeningRef.current = false;
      speechRecognitionRef.current = null;
      setSpeechError(message);
      setVoiceSessionActive(false);
      setVoiceState(VOICE_STATE.IDLE);
      try {
        recognition.stop();
      } catch (_) {
        // ignore
      }
    };

    recognition.onend = () => {
      if (!voiceListeningRef.current || speechRecognitionRef.current !== recognition) return;
      try {
        recognition.start();
      } catch (_) {
        // InvalidStateError if already active — safe to ignore
      }
    };

    voiceListeningRef.current = true;
    setSpeechError(null);

    try {
      recognition.start();
      return true;
    } catch (_) {
      voiceListeningRef.current = false;
      speechRecognitionRef.current = null;
      setSpeechError('Could not start speech recognition. Try again or type your question.');
      return false;
    }
  }, [speechSupported, stopSpeechRecognition, scheduleReturnToListening, syncChatInput]);

  const isVoiceMicSession =
    voiceState === VOICE_STATE.LISTENING || voiceState === VOICE_STATE.SPEAKING;

  useEffect(() => {
    saveHistory(messages);
  }, [messages]);

  useEffect(() => {
    return () => {
      clearSpeechPauseTimer();
      stopSpeechRecognition();
      stopTextToSpeech();
    };
  }, [clearSpeechPauseTimer, stopSpeechRecognition]);

  useEffect(() => {
    if (!isOpen) {
      stopSpeechRecognition();
      stopReadAloud();
      setVoiceState(VOICE_STATE.IDLE);
      setVoiceSessionActive(false);
      setSpeechError(null);
      setPendingUploadFile(null);
    }
  }, [isOpen, stopSpeechRecognition, stopReadAloud]);

  useEffect(() => {
    if (loading && voiceSessionActive) {
      setVoiceState(VOICE_STATE.PROCESSING);
    }
  }, [loading, voiceSessionActive]);

  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = loading;

    if (wasLoading && !loading && voiceSessionActive) {
      setVoiceState(VOICE_STATE.IDLE);
      setVoiceSessionActive(false);
    }
  }, [loading, voiceSessionActive]);

  const handleVoiceMicClick = () => {
    if (loading || voiceState === VOICE_STATE.PROCESSING) {
      return;
    }

    if (isVoiceMicSession) {
      stopSpeechRecognition();
      setVoiceSessionActive(false);
      setVoiceState(VOICE_STATE.IDLE);
      inputRef.current?.focus();
      return;
    }

    if (!speechSupported) {
      setSpeechError(
        'Voice input is not supported in this browser. Try Chrome, Edge, or Safari, or type your question.'
      );
      return;
    }

    stopReadAloud();
    setVoiceSessionActive(true);
    setVoiceState(VOICE_STATE.LISTENING);
    const started = startSpeechRecognition();
    if (!started) {
      setVoiceSessionActive(false);
      setVoiceState(VOICE_STATE.IDLE);
    }
  };

  const handleVoiceStop = () => {
    stopSpeechRecognition();
    setVoiceSessionActive(false);
    setVoiceState(VOICE_STATE.IDLE);
    inputRef.current?.focus();
  };

  const showVoiceStatus =
    voiceState === VOICE_STATE.LISTENING ||
    voiceState === VOICE_STATE.SPEAKING ||
    voiceState === VOICE_STATE.PROCESSING ||
    Boolean(speechError);
  const showVoiceStop = isVoiceMicSession;
  const voiceMicDisabled = loading || voiceState === VOICE_STATE.PROCESSING;

  // Revoke blob URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      (messagesRef.current || []).forEach((m) => {
        if (m && m.attachmentBlobUrl && typeof m.attachmentBlobUrl === 'string') {
          try {
            URL.revokeObjectURL(m.attachmentBlobUrl);
          } catch (_) { }
        }
      });
    };
  }, []);

  const scrollToBottom = (smooth = true) => {
    const el = messagesContainerRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
      setShowJumpToBottom(false);
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const el = messagesContainerRef.current;
    if (el) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.scrollTop = el.scrollHeight;
          setShowJumpToBottom(false);
        });
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    scrollToBottom(true);
  }, [messages]);

  useEffect(() => {
    if (!isOpen || !inputRef.current) return;
    const draft = inputTextRef.current || '';
    if (inputRef.current.value !== draft) {
      inputRef.current.value = draft;
    }
    syncInputHasTextFlag(draft);
    inputRef.current.focus();
  }, [isOpen, syncInputHasTextFlag]);

  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const threshold = 80;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
    setShowJumpToBottom((prev) => (atBottom ? false : true));
  };

  const CHAT_UPLOAD_MAX_BYTES = 35 * 1024 * 1024;

  const downloadTextFile = (content, filename) => {
    const mime = filename.endsWith('.json')
      ? 'application/json;charset=utf-8'
      : 'text/csv;charset=utf-8';
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleAttachClick = () => {
    if (loading) return;
    fileInputRef.current?.click();
  };

  const clearPendingUpload = () => {
    setPendingUploadFile(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || loading) return;
    if (file.size > CHAT_UPLOAD_MAX_BYTES) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          error: `File is too large (max ${Math.round(CHAT_UPLOAD_MAX_BYTES / (1024 * 1024))} MB).`,
        },
      ]);
      return;
    }
    setPendingUploadFile(file);
    inputRef.current?.focus();
  };

  const uploadPendingFile = (file, prompt) => {
    const { outputFormat, instruction } = inferFileUploadFromPrompt(prompt);
    const userText = prompt.trim()
      ? `${file.name}: ${prompt.trim()}`
      : `${file.name} (read as plain text)`;

    setMessages((prev) => [...prev, { role: 'user', text: userText }]);
    setLoadingMessage(
      outputFormat === 'raw'
        ? 'Reading file…'
        : outputFormat === 'text'
          ? 'Reading and analyzing…'
          : 'Extracting and generating…'
    );
    setLoading(true);

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const dataUrl = reader.result;
        if (typeof dataUrl !== 'string') {
          throw new Error('Could not read file.');
        }
        const comma = dataUrl.indexOf(',');
        const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
        const api = new DataApi('objectconfig');
        const res = await api.post('/ai-chat/parse-file', {
          fileBase64: base64,
          filename: file.name,
          outputFormat,
          instruction,
        });
        const data = res?.data;
        if (data?.success && typeof data.fullText === 'string') {
          const stats = data.stats;
          const summaryParts = [];
          if (data.kind === 'pdf' && data.pageCount != null) {
            summaryParts.push(`${data.pageCount} page${data.pageCount !== 1 ? 's' : ''}`);
            if (stats && typeof stats === 'object') {
              if (stats.textPages != null) summaryParts.push(`${stats.textPages} text`);
              if (stats.ocrPages != null) summaryParts.push(`${stats.ocrPages} OCR`);
              if (stats.visionPages != null) summaryParts.push(`${stats.visionPages} AI vision`);
            }
          } else if (data.charCount != null) {
            summaryParts.push(`${data.charCount.toLocaleString()} characters`);
          }
          if (
            data.transformedOutput != null &&
            (data.outputFormat === 'json' ||
              data.outputFormat === 'csv' ||
              data.outputFormat === 'text')
          ) {
            summaryParts.push(
              data.outputFormat === 'text' ? 'AI answer' : `AI → ${String(data.outputFormat).toUpperCase()}`
            );
          }
          const summary = summaryParts.length ? summaryParts.join(' · ') : 'Done';
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              documentParse: true,
              parseFilename: data.filename || file.name,
              parseKind: data.kind || 'file',
              parsedText: data.fullText,
              parseSummary: summary,
              transformedOutput:
                typeof data.transformedOutput === 'string' ? data.transformedOutput : undefined,
              resultFormat:
                data.outputFormat === 'json' ||
                  data.outputFormat === 'csv' ||
                  data.outputFormat === 'text'
                  ? data.outputFormat
                  : null,
              userInstruction: data.instruction || null,
            },
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              error: data?.message || 'Could not parse this file.',
            },
          ]);
        }
      } catch (err) {
        const msg =
          err?.response?.data?.message || err?.message || 'Failed to read file. Try another format.';
        setMessages((prev) => [...prev, { role: 'assistant', error: msg }]);
      } finally {
        setLoading(false);
        setLoadingMessage('Running query…');
      }
    };
    reader.onerror = () => {
      setMessages((prev) => [...prev, { role: 'assistant', error: 'Could not read file.' }]);
      setLoading(false);
      setLoadingMessage('Running query…');
    };
    reader.readAsDataURL(file);
  };

  /** Primary send entry — typed and voice-drafted text both use the input field. */
  const handleSend = async (overrideQuestion) => {
    if (loading) return;

    if (isVoiceMicSession || voiceListeningRef.current) {
      stopSpeechRecognition();
    }

    const fromSuggestion = typeof overrideQuestion === 'string';
    const question = (fromSuggestion ? overrideQuestion : inputTextRef.current || '').trim();
    const attachedFile = fromSuggestion ? null : pendingUploadFile;

    if (!question && !attachedFile) return;

    stopReadAloud();

    if (attachedFile) {
      setPendingUploadFile(null);
      syncChatInput('');
      if (voiceSessionActive) {
        setVoiceState(VOICE_STATE.PROCESSING);
      }
      uploadPendingFile(attachedFile, question);
      return;
    }

    if (voiceSessionActive) {
      setVoiceState(VOICE_STATE.PROCESSING);
    }

    if (!fromSuggestion) {
      syncChatInput('');
    }
    setMessages((prev) => [...prev, { role: 'user', text: question }]);
    setLoadingMessage('Running query…');
    setLoading(true);

    try {
      const isDocumentOrQuoteIntent = isDocumentOrQuoteIntentMessage(question);

      // Create record: waiting for user to choose object (after "create record" without object)
      if (pendingCreateObject) {
        setPendingCreateObject(false);
        const objectName = parseCreateObjectChoice(question) || (question.trim() ? question.trim().toLowerCase() : null);
        if (objectName) {
          const label = CREATE_RECORD_OBJECTS.find((o) => o.objectName === objectName)?.label || objectName;
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', text: `Opening form to create a new ${label} record.` },
          ]);
          setCreateModalObjectName(objectName);
          setShowCreateModal(true);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              error: 'Please choose an object type or type the object name.',
              quickReplyOptions: CREATE_RECORD_OBJECTS.map((o) => o.label),
              quickReplyAction: 'create',
            },
          ]);
          setPendingCreateObject(true);
        }
        setLoading(false);
        return;
      }

      // Create record intent (skip when user wants to generate/download a quote or document)
      const createIntent = detectCreateRecordIntent(question);
      if (createIntent.isCreateIntent && !isDocumentOrQuoteIntent) {
        const objectName = createIntent.objectName;
        if (objectName) {
          const label = CREATE_RECORD_OBJECTS.find((o) => o.objectName === objectName)?.label || objectName;
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', text: `Opening form to create a new ${label} record.` },
          ]);
          setCreateModalObjectName(objectName);
          setShowCreateModal(true);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              text: 'Which type of record do you want to create?',
              quickReplyOptions: CREATE_RECORD_OBJECTS.map((o) => o.label),
              quickReplyAction: 'create',
            },
          ]);
          setPendingCreateObject(true);
        }
        setLoading(false);
        return;
      }

      // Step 2: We have object type (Lead/Opportunity/Quote), now waiting for the record ID
      if (pendingDocumentStep === 'id' && pendingDocumentObjectName && !isDocumentOrQuoteIntent) {
        const objectName = pendingDocumentObjectName;
        setPendingDocumentStep(null);
        setPendingDocumentObjectName(null);
        const recordId = question.trim();
        if (!recordId) {
          const label = objectName === 'lead' ? 'Lead' : objectName === 'opportunity' ? 'Opportunity' : 'Quote';
          setMessages((prev) => [
            ...prev,
            { role: 'assistant', error: `Please provide the ${label} ID (you can copy it from the record URL or list).` },
          ]);
          setLoading(false);
          return;
        }
        const objectLabel = objectName === 'lead' ? 'Lead' : objectName === 'opportunity' ? 'Opportunity' : 'Quote';
        try {
          let result;
          if (objectName === 'lead') {
            result = await GenerateDocumentApi.generateQuoteFromLead(recordId, 'customer quote', { returnForDisplay: true });
          } else if (objectName === 'opportunity') {
            result = await GenerateDocumentApi.generateDocument('opportunity', recordId, 'customer quote', false, { returnForDisplay: true });
          } else {
            result = await GenerateDocumentApi.generateDocument('quote', recordId, 'customer quote', false, { returnForDisplay: true });
          }
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              text: `Document has been generated for the ${objectLabel} you specified. You can view or download it below.`,
              attachmentBlobUrl: result?.blobUrl,
              attachmentFilename: result?.filename || 'document.pdf',
            },
          ]);
        } catch (err) {
          const msg = err?.message || `Failed to generate document for that ${objectLabel}.`;
          setMessages((prev) => [...prev, { role: 'assistant', error: msg }]);
        }
        setLoading(false);
        return;
      }

      // Step 1: We asked "choose Lead / Opportunity / Quote"; user replied with their choice
      if (pendingDocumentStep === 'object' && !isDocumentOrQuoteIntent) {
        const choice = parseDocumentObjectChoice(question);
        if (choice) {
          setPendingDocumentObjectName(choice);
          setPendingDocumentStep('id');
          const label = choice === 'lead' ? 'Lead' : choice === 'opportunity' ? 'Opportunity' : 'Quote';
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              text: `Please provide the ${label} ID (you can copy it from the record URL or list).`,
            },
          ]);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              error: 'Please choose one:',
              quickReplyOptions: ['Lead', 'Opportunity', 'Quote'],
              quickReplyAction: 'document',
            },
          ]);
        }
        setLoading(false);
        return;
      }

      // Generate document/quote: use current page context if on a relevant detail page, else ask to choose object then ID
      if (isDocumentOrQuoteIntent) {
        clearPendingDocumentFlow();
        if (currentLeadId) {
          try {
            const result = await GenerateDocumentApi.generateQuoteFromLead(currentLeadId, 'customer quote', { returnForDisplay: true });
            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                text: 'Quote document has been generated for the lead you\'re viewing. You can view or download it below.',
                attachmentBlobUrl: result?.blobUrl,
                attachmentFilename: result?.filename || 'quote.pdf',
              },
            ]);
          } catch (err) {
            const msg = err?.message || 'Failed to generate quote document.';
            setMessages((prev) => [...prev, { role: 'assistant', error: msg }]);
          }
          setLoading(false);
          return;
        }
        if (currentOpportunityId) {
          try {
            const result = await GenerateDocumentApi.generateDocument('opportunity', currentOpportunityId, 'customer quote', false, { returnForDisplay: true });
            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                text: 'Document has been generated for the opportunity you\'re viewing. You can view or download it below.',
                attachmentBlobUrl: result?.blobUrl,
                attachmentFilename: result?.filename || 'opportunity_document.pdf',
              },
            ]);
          } catch (err) {
            const msg = err?.message || 'Failed to generate document.';
            setMessages((prev) => [...prev, { role: 'assistant', error: msg }]);
          }
          setLoading(false);
          return;
        }
        if (currentQuoteId) {
          try {
            const result = await GenerateDocumentApi.generateDocument('quote', currentQuoteId, 'customer quote', false, { returnForDisplay: true });
            setMessages((prev) => [
              ...prev,
              {
                role: 'assistant',
                text: 'Quote document has been generated for the quote you\'re viewing. You can view or download it below.',
                attachmentBlobUrl: result?.blobUrl,
                attachmentFilename: result?.filename || 'quote.pdf',
              },
            ]);
          } catch (err) {
            const msg = err?.message || 'Failed to generate document.';
            setMessages((prev) => [...prev, { role: 'assistant', error: msg }]);
          }
          setLoading(false);
          return;
        }
        // Not on a detail page: ask to choose object type first (with clickable options)
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text: 'Which record do you want to generate a document for?',
            quickReplyOptions: ['Lead', 'Opportunity', 'Quote'],
            quickReplyAction: 'document',
          },
        ]);
        setPendingDocumentStep('object');
        setLoading(false);
        return;
      }

      if (isGreetingMessage(question)) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text: `Hello! I'm ${CHATBOT_NAME}. How may I help you today? Choose an option below or ask your own question.`,
            quickReplyOptions: GREETING_QUICK_OPTIONS,
            quickReplyAction: 'suggestion',
          },
        ]);
        setLoading(false);
        return;
      }

      const wantsReportCatalog = detectListReportsIntent(question);
      const wantsDashboardCatalog = detectListDashboardsIntent(question);
      if (wantsReportCatalog || wantsDashboardCatalog) {
        setLoadingMessage('Loading catalog…');
        const catalogSections = [];
        let loadError = null;

        if (wantsReportCatalog) {
          try {
            const reportRes = await new DataApi('reports').get('/');
            const reportPayload = reportRes?.data;
            if (!reportPayload?.success) {
              loadError = reportPayload?.message || 'Failed to load reports.';
            } else {
              const reportRows = formatReportsForChatTable(reportPayload.data);
              catalogSections.push({
                title: reportRows.length > 0 ? `Reports (${reportRows.length})` : 'Reports',
                data: reportRows,
                columnLabels: REPORT_LIST_COLUMN_LABELS,
                catalogLink: CATALOG_LINK_REPORT,
              });
            }
          } catch (err) {
            loadError = err?.response?.data?.message || err?.message || 'Failed to load reports.';
          }
        }

        if (!loadError && wantsDashboardCatalog) {
          try {
            const dashboardRes = await new DataApi('dashboard').fetchAll();
            const dashboardPayload = dashboardRes?.data;
            const dashboardRows = formatDashboardsForChatTable(
              dashboardPayload?.success && Array.isArray(dashboardPayload.data)
                ? dashboardPayload.data
                : []
            );
            catalogSections.push({
              title: dashboardRows.length > 0 ? `Dashboards (${dashboardRows.length})` : 'Dashboards',
              data: dashboardRows,
              columnLabels: DASHBOARD_LIST_COLUMN_LABELS,
              catalogLink: CATALOG_LINK_DASHBOARD,
            });
          } catch (err) {
            loadError = err?.response?.data?.message || err?.message || 'Failed to load dashboards.';
          }
        }

        if (loadError) {
          setMessages((prev) => [...prev, { role: 'assistant', error: loadError }]);
          setLoading(false);
          return;
        }

        const totalRows = catalogSections.reduce((sum, s) => sum + (s.data?.length || 0), 0);
        const summaryParts = catalogSections.map((s) => {
          const count = s.data?.length || 0;
          const kind = s.title?.split(' ')[0] || 'Items';
          return `${count} ${kind.toLowerCase()}`;
        });
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text:
              totalRows > 0
                ? `Here is what you can access (${summaryParts.join(', ')}). Click a name to open it.`
                : 'You do not have any reports or dashboards yet, or none are shared with you.',
            rowCount: totalRows,
            catalogSections,
          },
        ]);
        setLoading(false);
        return;
      }

      // OpenAI path: DataQueryPlan → fetchRelatedRecords (SQL fallback for aggregates)
      const api = new DataApi('objectconfig');
      const res = await api.post('/ai-query/execute', { question });
      const data = res?.data;

      if (data?.notAQuery) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text: data?.message || `I'm here to help you query your CRM data. Ask a question or pick a suggestion below.`,
            quickReplyOptions: GREETING_QUICK_OPTIONS,
            quickReplyAction: 'suggestion',
          },
        ]);
        setLoading(false);
        return;
      }

      setMessages((prev) => [...prev, mapQueryApiResponseToMessage(data, question)]);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to run query. Please try again.';
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          error: msg,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /** When user clicks a quick-reply option (Lead / Opportunity / Quote) for document generation */
  const handleDocumentObjectChoice = (option) => {
    const objectName = normalizeDocumentObjectName(option) || option.trim().toLowerCase();
    setMessages((prev) => [
      ...prev,
      { role: 'user', text: option },
      {
        role: 'assistant',
        text: `Please provide the ${option} ID (you can copy it from the record URL or list).`,
      },
    ]);
    setPendingDocumentObjectName(objectName);
    setPendingDocumentStep('id');
  };

  /** When user clicks a greeting quick-reply suggestion, run it as their next message */
  const handleSuggestionChoice = (option) => {
    handleSend(option);
  };

  /** When user clicks a quick-reply option for create record (Lead, Opportunity, etc.) */
  const handleCreateObjectChoice = (option) => {
    const item = CREATE_RECORD_OBJECTS.find((o) => o.label === option);
    const objectName = item ? item.objectName : option.toLowerCase();
    setMessages((prev) => [
      ...prev,
      { role: 'user', text: option },
      { role: 'assistant', text: `Opening form to create a new ${option} record.` },
    ]);
    setCreateModalObjectName(objectName);
    setShowCreateModal(true);
    setPendingCreateObject(false);
  };

  const getColumnLabel = (col, columnLabels) => {
    if (columnLabels && columnLabels[col]) return columnLabels[col];
    return col.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const isFetchRecordsFieldCell = (cell) =>
    cell != null &&
    typeof cell === 'object' &&
    !Array.isArray(cell) &&
    !(cell instanceof Date) &&
    ('value' in cell || 'type' in cell || 'displayValue' in cell);

  /**
   * Format cell value for display. fetchRecords rows use DetailLayout/list currency rules.
   */
  const formatCellValue = (
    col,
    value,
    row,
    objectName,
    columnLabels,
    dataFormat,
    allRows,
    columnMeta
  ) => {
    if (value == null || value === '') return '';
    if (Array.isArray(value)) {
      return formatIncludeCellSummary(value, getColumnLabel(col, columnLabels));
    }
    const fieldCell = isFetchRecordsFieldCell(value) ? value : null;
    if (fieldCell && row) {
      return formatFetchRecordsFieldForDisplay(fieldCell, row, {
        ...currencyDisplayRef.current,
        objectName,
      });
    }
    // Avoid [object Object]: normalize interval-like or plain objects to a displayable value
    if (typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date)) {
      const intervalKeys = ['years', 'months', 'days', 'hours', 'minutes', 'seconds', 'milliseconds'];
      const hasInterval = intervalKeys.some((k) => Object.prototype.hasOwnProperty.call(value, k));
      if (hasInterval) {
        const d = Number(value.days) || 0;
        const h = Number(value.hours) || 0;
        const mi = Number(value.minutes) || 0;
        const totalDays = d + h / 24 + mi / 1440;
        return String(Math.round(totalDays));
      }
      if (typeof value.toPostgres === 'function') return value.toPostgres();
      if (typeof value.toISO === 'function') return value.toISO();
      return '[value]';
    }
    const str = String(value).trim();
    if (!str) return '';
    const colLower = (col || '').toLowerCase();
    const isPercentageOrFloat =
      colLower.includes('percentage') ||
      colLower.includes('percent') ||
      colLower.includes('allocation');
    if (isPercentageOrFloat) {
      const n = typeof value === 'number' ? value : parseFloat(value);
      if (Number.isFinite(n)) return n.toFixed(2);
    }
    const looksLikeDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(str);
    const looksLikeDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str) || /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(str);
    const looksLikeTimeOnly = /^\d{1,2}:\d{2}(:\d{2})?$/.test(str);
    if (colLower.includes('datetime') || colLower === 'createddate' || colLower === 'lastmodifieddate' || looksLikeDateTime) {
      try {
        return formatDateTime(value);
      } catch {
        return str;
      }
    }
    if (colLower.includes('_date') || colLower.endsWith('date') || colLower === 'due_date' || looksLikeDateOnly) {
      try {
        return formatDate(value);
      } catch {
        return str;
      }
    }
    if ((colLower.includes('time') && !colLower.includes('datetime')) || looksLikeTimeOnly) {
      try {
        return formatTime(value);
      } catch {
        return str;
      }
    }
    const aggregateCurrency = formatAiChatFlatAggregateCurrency(col, value, {
      columnLabels,
      currencyCtx: currencyDisplayRef.current,
      objectName,
      rows: allRows,
      dataFormat,
      columnMeta,
    });
    if (aggregateCurrency != null) return aggregateCurrency;
    return str;
  };

  speakHelpersRef.current = { getColumnLabel, formatCellValue };

  const renderResultChart = (
    rows,
    columnLabels,
    chartType = 'bar',
    height = 220,
    objectName
  ) => (
    <div className="ai-chatbot-chart-wrap">
      <AiChatEChart
        rows={rows}
        columnLabels={columnLabels}
        chartType={chartType}
        height={height}
        objectName={objectName}
        currencyCtx={currencyDisplayCtx}
      />
    </div>
  );


  const openChartFullView = (data, columnLabels, chartType, objectName, resultTitle) => {
    setFullView({
      type: 'chart',
      data,
      columnLabels,
      chartType: chartType || 'bar',
      objectName,
      resultTitle: resultTitle || null,
    });
  };

  const openTableFullView = (
    data,
    columnLabels,
    objectName,
    catalogLink,
    displayColumns,
    dataFormat,
    columnMeta,
    includeKeys,
    groupBy,
    resultTitle
  ) => {
    setFullView({
      type: 'table',
      data,
      columnLabels,
      objectName,
      catalogLink,
      displayColumns,
      dataFormat,
      columnMeta,
      includeKeys,
      groupBy,
      resultTitle: resultTitle || null,
    });
  };

  const getCatalogTableSections = (msg) => {
    if (msg?.catalogSections?.length) return msg.catalogSections;
    if (msg?.data) {
      return [
        {
          data: msg.data,
          columnLabels: msg.columnLabels,
          objectName: msg.objectName,
          catalogLink: msg.catalogLink,
          displayColumns: msg.displayColumns,
          dataFormat: msg.dataFormat,
        },
      ];
    }
    return [];
  };

  const renderReadAloudButton = (messageIndex, msg) => {
    if (!ttsSupported || !getAssistantSpeakableText(msg, speakHelpersRef.current)) return null;
    const isActive = ttsSpeakingIndex === messageIndex;
    return (
      <button
        type="button"
        className={`ai-chatbot-speak-btn${isActive ? ' ai-chatbot-speak-btn--active' : ''}`}
        onClick={() => handleToggleReadAloud(messageIndex, msg)}
        title={isActive ? 'Stop reading' : 'Read aloud'}
        aria-label={isActive ? 'Stop reading aloud' : 'Read response aloud'}
        aria-pressed={isActive}
      >
        {isActive ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
        ) : (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
          </svg>
        )}
      </button>
    );
  };

  const downloadPdf = async (messageIndex, msg) => {
    if (!msg?.data || !Array.isArray(msg.data) || msg.data.length === 0) return;
    const doc = new jsPDF('p', 'mm', 'a4');
    const margin = 14;
    let currentY = margin;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const maxWidth = pageWidth - 2 * margin;
    const resultTitle = msg.resultTitle || msg.text || 'Query Results';
    const dateStamp = new Date().toISOString().split('T')[0];

    try {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      const titleLines = doc.splitTextToSize(resultTitle, maxWidth);
      doc.text(titleLines, margin, currentY);
      currentY += titleLines.length * 7 + 4;

      if (msg.rowCount != null) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(
          `${msg.rowCount} row${msg.rowCount !== 1 ? 's' : ''} · ${dateStamp}`,
          margin,
          currentY
        );
        doc.setTextColor(0, 0, 0);
        currentY += 8;
      }

      if (msg.showChart) {
        const chartEl = document.querySelector(`[data-ai-chart-index="${messageIndex}"]`);
        if (chartEl) {
          const canvas = await html2canvas(chartEl, {
            backgroundColor: '#ffffff',
            scale: 2,
            useCORS: true,
            logging: false,
          });
          const imgData = canvas.toDataURL('image/png');
          const imgWidth = Math.min(maxWidth, (canvas.width * 0.4));
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          if (currentY + imgHeight + 10 > pageHeight - margin) {
            doc.addPage();
            currentY = margin;
          }
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.text('Chart', margin, currentY);
          currentY += 8;
          doc.addImage(imgData, 'PNG', margin, currentY, imgWidth, imgHeight);
          currentY += imgHeight + 14;
        }
      }

      const columns = resolveQueryTableColumns(
        msg.data,
        msg.displayColumns,
        msg.includeKeys,
        { dataFormat: msg.dataFormat, groupBy: msg.groupBy }
      );
      if (columns.length === 0) return;

      const headers = columns.map((col) => getColumnLabel(col, msg.columnLabels));
      const body = msg.data.map((row) =>
        columns.map((col) => {
          const val = row[col];
          if (val == null || val === '') return '';
          return String(
            formatCellValue(
              col,
              val,
              row,
              msg.objectName,
              msg.columnLabels,
              msg.dataFormat,
              msg.data,
              msg.columnMeta
            )
          );
        })
      );

      if (currentY + 20 > pageHeight - margin) {
        doc.addPage();
        currentY = margin;
      }

      autoTable(doc, {
        head: [headers],
        body,
        startY: currentY,
        margin: { left: margin, right: margin },
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [70, 100, 140], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 245] },
      });

      const fileName = `${sanitizeResultTitleForFilename(resultTitle)}_${dateStamp}.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error('PDF export failed', err);
    }
  };

  const renderTableCellLink = (linkUrl, value) => {
    const label = value || '\u00A0';
    if (!linkUrl) return label;
    if (linkUrl.startsWith('/')) {
      return (
        <Link to={linkUrl} className="text-primary text-decoration-underline">
          {label}
        </Link>
      );
    }
    return (
      <a
        href={linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary text-decoration-underline"
      >
        {label}
      </a>
    );
  };

  const renderNestedIncludes = (rows, includeKeys, columnLabels) => {
    const keys = detectNestedIncludeKeys(rows, includeKeys);
    if (!keys.length || !Array.isArray(rows)) return null;

    return keys.map((includeKey) => {
      const sections = rows
        .map((parent) => {
          const children = parent?.[includeKey];
          if (!Array.isArray(children) || children.length === 0) return null;
          return { parent, children };
        })
        .filter(Boolean);
      if (!sections.length) return null;

      const sectionLabel = getColumnLabel(includeKey, columnLabels);
      return (
        <div key={includeKey} className="ai-chatbot-nested-include mt-3">
          <div className="ai-chatbot-nested-include-title small fw-semibold mb-2">{sectionLabel}</div>
          {sections.map(({ parent, children }, idx) => {
            const parentLabel =
              parent?.name?.value ??
              parent?.name ??
              parent?.title?.value ??
              parent?.title ??
              `Record ${idx + 1}`;
            return (
              <div key={`${includeKey}-${idx}`} className="mb-2">
                <div className="small text-muted mb-1">{parentLabel}</div>
                {renderResultTable(
                  children,
                  columnLabels,
                  null,
                  null,
                  null,
                  'fetchRecords',
                  []
                )}
              </div>
            );
          })}
        </div>
      );
    });
  };

  const renderResultTable = (
    rows,
    columnLabels,
    objectName,
    catalogLink,
    displayColumns,
    dataFormat,
    includeKeys,
    columnMeta,
    groupBy
  ) => {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const catalogConfig = resolveCatalogLinkConfig(catalogLink);
    const columns = resolveQueryTableColumns(rows, displayColumns, includeKeys, {
      dataFormat,
      groupBy,
    });
    if (columns.length === 0) return <p className="mb-0 text-muted">No columns</p>;

    const isGroupedAggregate =
      (dataFormat === 'aggregate' || dataFormat === 'currencyAggregate') &&
      Array.isArray(groupBy) &&
      groupBy.length > 0;
    const summableCols = columns.filter((col) =>
      columnSupportsTableTotal(col, rows, objectName, columnLabels, columnMeta)
    );
    const showTotals =
      !isGroupedAggregate && rows.length > 1 && summableCols.length > 0;
    let totalLabelPlaced = false;

    return (
      <div className="ai-chatbot-table-wrap">
        <Table size="sm" bordered hover responsive className="ai-chatbot-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col}>{getColumnLabel(col, columnLabels)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx}>
                {columns.map((col) => {
                  const cell = row[col];
                  const value = formatCellValue(
                    col,
                    cell,
                    row,
                    objectName,
                    columnLabels,
                    dataFormat,
                    rows,
                    columnMeta
                  );
                  const rowId = row?.id?.value ?? row?.id;
                  const isNameCol = col === 'name';
                  const isRelatedRecordCol = col === 'related_record';
                  const canLinkName = isNameCol && objectName && rowId != null && rowId !== '';
                  const relatedObject = row.related_object?.value ?? row.related_object;
                  const relatedRecordId = row.related_record_id?.value ?? row.related_record_id;
                  const canLinkRelatedRecord =
                    isRelatedRecordCol &&
                    relatedObject &&
                    relatedRecordId != null &&
                    relatedRecordId !== '';
                  let catalogUrl =
                    catalogConfig &&
                    catalogConfig.labelColumn === col &&
                    row.id != null &&
                    row.id !== ''
                      ? catalogConfig.getUrl(row)
                      : null;
                  if (!catalogUrl && row.id != null && row.id !== '') {
                    if (col === 'report_label') {
                      catalogUrl = CATALOG_LINK_CONFIGS[CATALOG_LINK_REPORT].getUrl(row);
                    } else if (col === 'dashboard_label') {
                      catalogUrl = CATALOG_LINK_CONFIGS[CATALOG_LINK_DASHBOARD].getUrl(row);
                    }
                  }
                  const linkUrl = catalogUrl
                    || (canLinkName
                      ? `/view/${objectName}/${rowId}`
                      : canLinkRelatedRecord
                        ? `/view/${relatedObject}/${relatedRecordId}`
                        : null);
                  return (
                    <td key={col}>
                      {renderTableCellLink(linkUrl, value)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          {showTotals && (
            <tfoot>
              <tr className="ai-chatbot-table-total-row">
                {columns.map((col) => {
                  if (summableCols.includes(col)) {
                    const totalText = formatAiChatTableTotal(
                      col,
                      rows,
                      objectName,
                      columnLabels,
                      currencyDisplayCtx,
                      dataFormat,
                      columnMeta
                    );
                    return (
                      <td key={col} className="text-end fw-semibold">
                        {totalText}
                      </td>
                    );
                  }
                  if (!totalLabelPlaced) {
                    totalLabelPlaced = true;
                    return (
                      <td key={col} className="fw-semibold">
                        Total
                      </td>
                    );
                  }
                  return <td key={col} />;
                })}
              </tr>
            </tfoot>
          )}
        </Table>
      </div>
    );
  };

  const renderCatalogTables = (msg) => {
    const sections = getCatalogTableSections(msg);
    if (!sections.length) return null;
    return sections.map((section, sectionIndex) => (
      <div
        key={section.title || `catalog-section-${sectionIndex}`}
        className={sectionIndex > 0 ? 'mt-3' : ''}
      >
        {section.title && <p className="mb-1 fw-semibold small">{section.title}</p>}
        {section.data?.length
          ? renderResultTable(
            section.data,
            section.columnLabels,
            section.objectName,
            section.catalogLink,
            section.displayColumns,
            section.dataFormat,
            section.includeKeys
          )
          : <p className="mb-0 text-muted small">None available.</p>}
      </div>
    ));
  };

  const anchorClassName = [
    'ai-chatbot-anchor',
    fabAlignStart ? 'ai-chatbot-anchor--align-start' : '',
    panelOpensBelow ? 'ai-chatbot-anchor--panel-below' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div
        ref={fabAnchorRef}
        className={anchorClassName}
        style={{ right: fabPosition.right, bottom: fabPosition.bottom }}
      >
        <button
          type="button"
          className={`ai-chatbot-fab${fabDragging ? ' ai-chatbot-fab--dragging' : ''}`}
          onPointerDown={handleFabPointerDown}
          onPointerMove={handleFabPointerMove}
          onPointerUp={handleFabPointerUp}
          onPointerCancel={handleFabPointerCancel}
          title={`${CHATBOT_LAUNCH_LABEL} — drag to move, click to open`}
          aria-label={`${CHATBOT_LAUNCH_LABEL}, ${CHATBOT_TAGLINE}. Drag to reposition.`}
        >
          <span className="ai-chatbot-fab-icon" aria-hidden>
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </span>
          <span className="ai-chatbot-fab-label">{CHATBOT_LAUNCH_LABEL}</span>
        </button>

        {isOpen && (
          <div className="ai-chatbot-panel">
            <div className="ai-chatbot-header">
              <div className="ai-chatbot-header-brand">
                <span className="ai-chatbot-title">{CHATBOT_NAME}</span>
                <span className="ai-chatbot-subtitle">{CHATBOT_TAGLINE}</span>
              </div>
              <span className="ai-chatbot-header-actions">
                {messages.length > 0 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="ai-chatbot-clear"
                    onClick={() => {
                      setMessages([]);
                      saveHistory([]);
                    }}
                    aria-label="Clear history"
                  >
                    Clear
                  </Button>
                )}
                <Button
                  variant="link"
                  size="sm"
                  className="ai-chatbot-close"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close"
                >
                  ×
                </Button>
              </span>
            </div>

            <div
              ref={messagesContainerRef}
              className="ai-chatbot-messages"
              onScroll={handleMessagesScroll}
            >
              {messages.length === 0 && (
                <div className="ai-chatbot-welcome">
                  <p>Ask a question in plain language to query your CRM data.</p>
                  <p className="small text-muted">e.g. &quot;List all contacts in Billing accounts&quot;</p>
                  <p className="small text-muted">
                    Attach a <strong>PDF</strong> or text file, then type what you want (e.g. &quot;give me JSON output&quot;,
                    &quot;extract the billing section&quot;) and press <strong>Send</strong>. Send with no message to read plain text.
                  </p>
                  <p className="small text-muted">
                    Say &quot;chart&quot;, &quot;graph&quot;, or name a type — column, bar, line, area, stacked, pie, donut, funnel, or{' '}
                    <strong>column chart with win % line</strong> for bars plus a trend line — e.g. &quot;Closed won vs lost by owner as column chart with win percentage line&quot;.
                  </p>
                  <p className="small text-muted">Results show key fields by default. Ask for specific fields if you need more, e.g. &quot;tasks with description and due date&quot;.</p>
                  <p className="small text-muted">
                    On a lead, opportunity, or quote record, say <strong>generate quote</strong> or <strong>download document</strong> to
                    create a PDF from the current record (or choose Lead / Opportunity / Quote and paste the record ID).
                  </p>
                </div>
              )}
              {messages.map((msg, i) => (
                <div key={i} className={`ai-chatbot-msg ai-chatbot-msg-${msg.role}`}>
                  {msg.role === 'user' && <div className="ai-chatbot-bubble user">{msg.text}</div>}
                  {msg.role === 'assistant' && (
                    <div className="ai-chatbot-bubble assistant">
                      {msg.documentParse && (msg.parsedText != null || msg.transformedOutput != null) && (
                        <div className="ai-chatbot-parse-result">
                          <div className="ai-chatbot-speakable-wrap ai-chatbot-speakable-wrap--header">
                            <p className="mb-1 fw-semibold">
                              {msg.parseFilename || 'file'}
                              {msg.parseSummary && (
                                <span className="fw-normal text-muted ms-1">({msg.parseSummary})</span>
                              )}
                            </p>
                            {renderReadAloudButton(i, msg)}
                          </div>
                          {msg.userInstruction && (
                            <p className="small text-muted mb-1">
                              <span className="fw-semibold text-body">Your instruction: </span>
                              {msg.userInstruction}
                            </p>
                          )}
                          {msg.transformedOutput != null && msg.resultFormat && (
                            <>
                              <p className="small fw-semibold mb-1 mt-2">
                                Result (
                                {msg.resultFormat === 'json'
                                  ? 'JSON'
                                  : msg.resultFormat === 'csv'
                                    ? 'CSV'
                                    : 'AI answer'}
                                )
                              </p>
                              <div className="ai-chatbot-parse-actions mb-2">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-1"
                                  onClick={() => {
                                    if (navigator.clipboard?.writeText) {
                                      navigator.clipboard.writeText(msg.transformedOutput).catch(() => { });
                                    }
                                  }}
                                >
                                  Copy result
                                </Button>
                                {(msg.resultFormat === 'json' || msg.resultFormat === 'csv') && (
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="me-1"
                                    onClick={() => {
                                      const ext = msg.resultFormat === 'json' ? 'json' : 'csv';
                                      const base = (msg.parseFilename || 'export').replace(/\.[^.]+$/, '');
                                      downloadTextFile(msg.transformedOutput, `${base}.${ext}`);
                                    }}
                                  >
                                    Download .{msg.resultFormat}
                                  </Button>
                                )}
                              </div>
                              <pre className="ai-chatbot-parse-pre ai-chatbot-parse-pre--result">
                                {msg.transformedOutput}
                              </pre>
                            </>
                          )}
                          {msg.parsedText != null && msg.parsedText !== '' && msg.resultFormat !== 'text' && (
                            <>
                              <p className="small fw-semibold mb-1 mt-2">
                                {msg.transformedOutput != null
                                  ? 'Extracted source text'
                                  : 'Extracted text'}
                              </p>
                              <div className="ai-chatbot-parse-actions mb-2">
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="me-1"
                                  onClick={() => {
                                    const t = msg.parsedText || '';
                                    if (navigator.clipboard?.writeText) {
                                      navigator.clipboard.writeText(t).catch(() => { });
                                    }
                                  }}
                                >
                                  Copy extracted
                                </Button>
                              </div>
                              <pre
                                className={
                                  msg.transformedOutput != null
                                    ? 'ai-chatbot-parse-pre ai-chatbot-parse-pre--source'
                                    : 'ai-chatbot-parse-pre'
                                }
                              >
                                {msg.parsedText || ''}
                              </pre>
                            </>
                          )}
                        </div>
                      )}
                      {msg.error && !msg.quickReplyOptions && (
                        <div className="ai-chatbot-speakable-wrap">
                          {renderReadAloudButton(i, msg)}
                          <p className="mb-0 text-danger">{msg.error}</p>
                        </div>
                      )}
                      {msg.text && !msg.error && !msg.documentParse && !(msg.resultTitle && msg.data) && (
                        <div className="ai-chatbot-speakable-wrap">
                          {renderReadAloudButton(i, msg)}
                          <p className="mb-1">
                            {msg.text}
                            {msg.rowCount != null && (
                              <span className="text-muted ms-1">
                                ({msg.rowCount} row{msg.rowCount !== 1 ? 's' : ''})
                              </span>
                            )}
                          </p>
                        </div>
                      )}
                      {msg.error && msg.quickReplyOptions && (
                        <div className="ai-chatbot-speakable-wrap">
                          {renderReadAloudButton(i, msg)}
                          <p className="mb-2 text-danger">{msg.error}</p>
                        </div>
                      )}
                      {msg.quickReplyOptions && Array.isArray(msg.quickReplyOptions) && (
                        <div className="ai-chatbot-quick-replies mt-2">
                          {msg.quickReplyOptions.map((option) => (
                            <Button
                              key={option}
                              variant="outline-primary"
                              size="sm"
                              className="ai-chatbot-quick-reply-btn me-1 mb-1"
                              onClick={() => {
                                if (msg.quickReplyAction === 'create') {
                                  handleCreateObjectChoice(option);
                                } else if (msg.quickReplyAction === 'suggestion') {
                                  handleSuggestionChoice(option);
                                } else {
                                  handleDocumentObjectChoice(option);
                                }
                              }}
                            >
                              {option}
                            </Button>
                          ))}
                        </div>
                      )}
                      {msg.attachmentBlobUrl && (
                        <div className="ai-chatbot-doc-attachment mt-2">
                          <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              as="a"
                              href={msg.attachmentBlobUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View PDF
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              as="a"
                              href={msg.attachmentBlobUrl}
                              download={msg.attachmentFilename || 'quote.pdf'}
                            >
                              Download
                            </Button>
                          </div>
                          <div className="ai-chatbot-pdf-embed-wrapper">
                            <iframe
                              title="Generated quote document"
                              src={msg.attachmentBlobUrl}
                              className="ai-chatbot-pdf-embed"
                            />
                          </div>
                        </div>
                      )}
                      {msg.attachmentFilename && !msg.attachmentBlobUrl && (
                        <p className="small text-muted mb-0 mt-1">
                          (Document was generated in a previous session. Generate again to view or download.)
                        </p>
                      )}
                      {(msg.data || msg.catalogSections?.length) && (
                        <>
                          {msg.resultTitle && msg.data && !msg.catalogSections?.length && (
                            <h6 className="ai-chatbot-result-title mb-2">{msg.resultTitle}</h6>
                          )}
                          {msg.showChart && (
                            <div className="ai-chatbot-result-block">
                              <div
                                data-ai-chart-index={i}
                                className="ai-chatbot-chart-clickable"
                                role="button"
                                tabIndex={0}
                                title="Open chart full view"
                                onClick={() => openChartFullView(
                                  msg.data,
                                  msg.columnLabels,
                                  msg.chartType || 'bar',
                                  msg.objectName,
                                  msg.resultTitle
                                )}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    openChartFullView(
                                      msg.data,
                                      msg.columnLabels,
                                      msg.chartType || 'bar',
                                      msg.objectName,
                                      msg.resultTitle
                                    );
                                  }
                                }}
                              >
                                {renderResultChart(
                                  msg.data,
                                  msg.columnLabels,
                                  msg.chartType || 'bar',
                                  220,
                                  msg.objectName
                                )}
                              </div>
                            </div>
                          )}
                          <div className="ai-chatbot-result-block">
                            {msg.catalogSections?.length
                              ? renderCatalogTables(msg)
                              : (
                                <>
                                  {renderResultTable(
                                    msg.data,
                                    msg.columnLabels,
                                    msg.objectName,
                                    msg.catalogLink,
                                    msg.displayColumns,
                                    msg.dataFormat,
                                    msg.includeKeys,
                                    msg.columnMeta,
                                    msg.groupBy
                                  )}
                                  {renderNestedIncludes(
                                    msg.data,
                                    msg.includeKeys,
                                    msg.columnLabels
                                  )}
                                </>
                              )}
                            {msg.data && !msg.catalogSections?.length && (
                              <div className="ai-chatbot-result-actions ai-chatbot-result-actions--bottom">
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 text-muted"
                                  onClick={() => openTableFullView(
                                    msg.data,
                                    msg.columnLabels,
                                    msg.objectName,
                                    msg.catalogLink,
                                    msg.displayColumns,
                                    msg.dataFormat,
                                    msg.columnMeta,
                                    msg.includeKeys,
                                    msg.groupBy,
                                    msg.resultTitle
                                  )}
                                >
                                  Full view
                                </Button>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 text-muted ms-2"
                                  onClick={() => downloadPdf(i, msg)}
                                >
                                  Download PDF
                                </Button>
                                {!msg.text && !msg.error && !msg.documentParse && (
                                  <span className="ai-chatbot-result-speak ms-2">
                                    {renderReadAloudButton(i, msg)}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="ai-chatbot-msg ai-chatbot-msg-assistant">
                  <div className="ai-chatbot-bubble assistant">
                    <span className="ai-chatbot-typing">{loadingMessage}</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
              {showJumpToBottom && (
                <button
                  type="button"
                  className="ai-chatbot-jump-to-bottom"
                  onClick={() => scrollToBottom(true)}
                  aria-label="Jump to bottom"
                  title="Jump to bottom"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 5v14M19 12l-7 7-7-7" />
                  </svg>
                  <span>Bottom</span>
                </button>
              )}
            </div>

            {(showVoiceStatus || speechError) && (
              <div
                className={`ai-chatbot-voice-status ai-chatbot-voice-status--${speechError ? 'error' : voiceState}`}
                role="status"
                aria-live="polite"
                aria-label={speechError || VOICE_STATUS_LABELS[voiceState]}
              >
                {speechError ? (
                  <span className="ai-chatbot-voice-status-label ai-chatbot-voice-status-label--error">
                    {speechError}
                  </span>
                ) : (
                  <>
                    {voiceState === VOICE_STATE.LISTENING && (
                      <span className="ai-chatbot-voice-indicator ai-chatbot-voice-indicator--listening" aria-hidden>
                        <span className="ai-chatbot-voice-pulse" />
                        <span className="ai-chatbot-voice-pulse ai-chatbot-voice-pulse--delay" />
                        <span className="ai-chatbot-voice-pulse ai-chatbot-voice-pulse--delay2" />
                      </span>
                    )}
                    {voiceState === VOICE_STATE.SPEAKING && (
                      <span className="ai-chatbot-voice-indicator ai-chatbot-voice-indicator--speaking" aria-hidden>
                        <span className="ai-chatbot-voice-bar" />
                        <span className="ai-chatbot-voice-bar ai-chatbot-voice-bar--t2" />
                        <span className="ai-chatbot-voice-bar ai-chatbot-voice-bar--t3" />
                        <span className="ai-chatbot-voice-bar ai-chatbot-voice-bar--t4" />
                      </span>
                    )}
                    {voiceState === VOICE_STATE.PROCESSING && (
                      <span className="ai-chatbot-voice-indicator ai-chatbot-voice-indicator--processing" aria-hidden>
                        <span className="ai-chatbot-voice-dot" />
                        <span className="ai-chatbot-voice-dot ai-chatbot-voice-dot--delay" />
                        <span className="ai-chatbot-voice-dot ai-chatbot-voice-dot--delay2" />
                      </span>
                    )}
                    <span className="ai-chatbot-voice-status-label">
                      {VOICE_STATUS_LABELS[voiceState]}
                    </span>
                  </>
                )}
              </div>
            )}

            <div className="ai-chatbot-footer">
              {pendingUploadFile && (
                <div className="ai-chatbot-pending-attach">
                  <span className="ai-chatbot-pending-attach-name" title={pendingUploadFile.name}>
                    📎 {pendingUploadFile.name}
                  </span>
                  <button
                    type="button"
                    className="ai-chatbot-pending-attach-remove"
                    onClick={clearPendingUpload}
                    disabled={loading}
                    aria-label="Remove attached file"
                    title="Remove file"
                  >
                    ×
                  </button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                className="d-none"
                accept=".pdf,.txt,.csv,.md,.json,.tsv,.log,application/pdf,text/plain,text/csv"
                onChange={handleFileChange}
              />
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                className="ai-chatbot-attach"
                onClick={handleAttachClick}
                disabled={loading}
                title="Attach PDF or text file"
                aria-label="Attach PDF or text file"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
              </Button>
              <Form.Control
                ref={inputRef}
                type="text"
                placeholder={
                  pendingUploadFile
                    ? 'e.g. give me JSON output, extract the totals section… (Send with empty = plain text)'
                    : speechSupported
                      ? 'Ask a question… or tap the mic to dictate a draft'
                      : 'Ask a question… (e.g. include description if you need extra fields)'
                }
                defaultValue=""
                onChange={(e) => {
                  const value = e.target.value;
                  inputTextRef.current = value;
                  syncInputHasTextFlag(value);
                  if (isVoiceMicSession) {
                    speechBaseInputRef.current = value;
                    speechSessionFinalRef.current = '';
                    clearSpeechPauseTimer();
                    if (voiceListeningRef.current) {
                      setVoiceState(VOICE_STATE.LISTENING);
                    }
                  }
                }}
                onKeyDown={handleKeyDown}
                disabled={loading}
                className="ai-chatbot-input"
              />
              <Button
                type="button"
                variant={isVoiceMicSession ? 'primary' : 'outline-secondary'}
                size="sm"
                className={`ai-chatbot-voice-mic${isVoiceMicSession ? ' ai-chatbot-voice-mic--active' : ''}`}
                onClick={handleVoiceMicClick}
                disabled={voiceMicDisabled}
                title={
                  !speechSupported
                    ? 'Voice input is not supported in this browser'
                    : isVoiceMicSession
                      ? 'Stop voice input'
                      : 'Start voice input'
                }
                aria-label={
                  !speechSupported
                    ? 'Voice input not supported'
                    : isVoiceMicSession
                      ? 'Stop voice input'
                      : 'Start voice input'
                }
                aria-pressed={isVoiceMicSession}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill={isVoiceMicSession ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                  <line x1="12" x2="12" y1="19" y2="22" />
                </svg>
              </Button>
              {showVoiceStop && (
                <Button
                  type="button"
                  variant="outline-danger"
                  size="sm"
                  className="ai-chatbot-voice-stop"
                  onClick={handleVoiceStop}
                  title="Stop voice"
                  aria-label="Stop voice"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden
                  >
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                  </svg>
                </Button>
              )}
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleSend()}
                disabled={loading || (!inputHasText && !pendingUploadFile)}
                className="ai-chatbot-send"
              >
                Send
              </Button>
            </div>
          </div>
        )}
      </div>

      <Modal
        show={fullView != null}
        onHide={() => setFullView(null)}
        size="xl"
        centered
        className="ai-chatbot-fullview-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {fullView?.resultTitle ||
              (fullView?.type === 'chart' ? 'Chart' : 'Table')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="ai-chatbot-fullview-body">
          {fullView?.type === 'chart' && fullView.data && (
            <div className="ai-chatbot-chart-wrap ai-chatbot-chart-fullview">
              {renderResultChart(
                fullView.data,
                fullView.columnLabels,
                fullView.chartType || 'bar',
                420,
                fullView.objectName
              )}
            </div>
          )}
          {fullView?.type === 'table' && fullView.data && (
            <div className="ai-chatbot-table-wrap ai-chatbot-table-fullview">
              {renderResultTable(
                fullView.data,
                fullView.columnLabels,
                fullView.objectName,
                fullView.catalogLink,
                fullView.displayColumns,
                fullView.dataFormat,
                fullView.includeKeys,
                fullView.columnMeta,
                fullView.groupBy
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {showCreateModal && createModalObjectName && (
        <EditLayoutModal
          show={showCreateModal}
          onHide={() => {
            setShowCreateModal(false);
            setCreateModalObjectName(null);
          }}
          objectName={createModalObjectName}
          onSave={() => {
            setShowCreateModal(false);
            setCreateModalObjectName(null);
          }}
          onCancel={() => {
            setShowCreateModal(false);
            setCreateModalObjectName(null);
          }}
        />
      )}
    </>
  );
};

export default AiChatbot;
